/**
 * 検出力 (power)、必要サンプルサイズ、最小検出可能効果 (MDE)。
 *
 * すべて 2 群の比率比較を前提とし、割付比 k = n2 / n1 に対応する。
 */

import { normalCdf, normalQuantile } from './normal'
import type { Tail } from './types'

/** 有意水準 alpha と裾から、棄却域の臨界値を返す。 */
function alphaCriticalValue(alpha: number, tail: Tail): number {
  return tail === 'two-sided'
    ? normalQuantile(1 - alpha / 2)
    : normalQuantile(1 - alpha)
}

export interface PowerInput {
  /** 対照群の真の比率 */
  baselineRate: number
  /** 比較群の真の比率 */
  targetRate: number
  n1: number
  n2: number
  alpha: number
  tail: Tail
}

/**
 * 与えられたサンプルサイズと効果量での検出力。
 *
 * 帰無仮説のもとでのプールした標準誤差で棄却域を決め、
 * 対立仮説のもとでのプールしない標準誤差で棄却確率を求める。
 */
export function power(input: PowerInput): number {
  const { baselineRate: p1, targetRate: p2, n1, n2, alpha, tail } = input
  if (n1 <= 0 || n2 <= 0) return NaN

  const delta = p2 - p1
  const pooled = (n1 * p1 + n2 * p2) / (n1 + n2)
  const se0 = Math.sqrt(pooled * (1 - pooled) * (1 / n1 + 1 / n2))
  const se1 = Math.sqrt((p1 * (1 - p1)) / n1 + (p2 * (1 - p2)) / n2)

  if (se1 <= 0) return delta === 0 ? alpha : 1
  if (se0 <= 0) return delta === 0 ? alpha : 1

  const zCrit = alphaCriticalValue(alpha, tail)

  if (tail === 'two-sided') {
    // 両側では反対方向に棄却される確率も加える（通常は無視できるほど小さい）
    const upper = normalCdf((delta - zCrit * se0) / se1)
    const lower = normalCdf((-delta - zCrit * se0) / se1)
    return Math.min(1, upper + lower)
  }
  if (tail === 'greater') {
    return normalCdf((delta - zCrit * se0) / se1)
  }
  return normalCdf((-delta - zCrit * se0) / se1)
}

export interface SampleSizeInput {
  baselineRate: number
  targetRate: number
  alpha: number
  /** 目標検出力 (例: 0.8) */
  power: number
  /** 割付比 n2 / n1。1 なら均等割付。 */
  allocationRatio: number
  tail: Tail
}

export interface SampleSizeResult {
  /** 対照群に必要な人数 */
  n1: number
  /** 比較群に必要な人数 */
  n2: number
  total: number
}

/**
 * 目標検出力を満たす必要サンプルサイズ。
 *
 * n1 = ( z_alpha * sqrt(p̄q̄(1 + 1/k)) + z_beta * sqrt(p1q1 + p2q2/k) )^2 / delta^2
 *
 * 連続修正は行わない（A/B テストの実務規模では影響が無視できるため）。
 */
export function requiredSampleSize(
  input: SampleSizeInput,
): SampleSizeResult | null {
  const {
    baselineRate: p1,
    targetRate: p2,
    alpha,
    power: targetPower,
    allocationRatio: k,
    tail,
  } = input

  const delta = p2 - p1
  if (delta === 0 || !Number.isFinite(delta)) return null
  if (k <= 0 || !Number.isFinite(k)) return null
  if (targetPower <= alpha || targetPower >= 1) return null

  const zAlpha = alphaCriticalValue(alpha, tail)
  const zBeta = normalQuantile(targetPower)

  const pooled = (p1 + k * p2) / (1 + k)
  const termNull = zAlpha * Math.sqrt(pooled * (1 - pooled) * (1 + 1 / k))
  const termAlt =
    zBeta * Math.sqrt(p1 * (1 - p1) + (p2 * (1 - p2)) / k)

  const n1 = Math.ceil((termNull + termAlt) ** 2 / (delta * delta))
  const n2 = Math.ceil(k * n1)
  return { n1, n2, total: n1 + n2 }
}

export interface MdeInput {
  baselineRate: number
  n1: number
  n2: number
  alpha: number
  /** 目標検出力 (例: 0.8) */
  power: number
  tail: Tail
  /** 検出したい方向。'increase' なら比率が上がる側の MDE。 */
  direction: 'increase' | 'decrease'
}

export interface MdeResult {
  /** 絶対差の MDE（比率のポイント差） */
  absolute: number
  /** ベースラインに対する相対 MDE */
  relative: number
  /** MDE を達成したときの比較群の比率 */
  targetRate: number
}

/**
 * 与えられたサンプルサイズで検出できる最小の効果量 (MDE)。
 *
 * power(delta) は |delta| について単調増加なので二分探索で解く。
 * 解析解を使わないのは、プールした標準誤差が delta に依存するため。
 */
export function minimumDetectableEffect(input: MdeInput): MdeResult | null {
  const {
    baselineRate: p1,
    n1,
    n2,
    alpha,
    power: targetPower,
    tail,
    direction,
  } = input

  if (n1 <= 0 || n2 <= 0) return null
  if (targetPower <= alpha || targetPower >= 1) return null
  if (p1 < 0 || p1 > 1) return null

  const sign = direction === 'increase' ? 1 : -1
  // 到達できる比率の上限（increase なら 1、decrease なら 0）までを探索範囲にする
  const maxDelta = direction === 'increase' ? 1 - p1 : p1
  if (maxDelta <= 0) return null

  const powerAt = (magnitude: number): number =>
    power({
      baselineRate: p1,
      targetRate: p1 + sign * magnitude,
      n1,
      n2,
      alpha,
      tail,
    })

  // 探索範囲の端でも目標検出力に届かないなら、このサンプルサイズでは検出不能
  if (powerAt(maxDelta) < targetPower) return null

  let low = 0
  let high = maxDelta
  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2
    if (powerAt(mid) < targetPower) {
      low = mid
    } else {
      high = mid
    }
    if (high - low < 1e-12) break
  }

  const absolute = sign * high
  return {
    absolute,
    relative: p1 > 0 ? absolute / p1 : NaN,
    targetRate: p1 + absolute,
  }
}
